import { z } from 'zod';
import { dateOrTimeSchema } from './content-primitives.ts';
import type { JsonLdObject } from './json-ld.ts';
import type { JsonValue } from './json.ts';

const emailSchema = z.email();
const timeProperties = new Set(['opens', 'closes', 'cutoffTime', 'checkinTime', 'checkoutTime']);
const nonnegativeProperties = new Set([
  'price',
  'lowPrice',
  'highPrice',
  'restockingFee',
  'returnShippingFeesAmount',
  'customerRemorseReturnShippingFeesAmount',
  'itemDefectReturnShippingFeesAmount',
  'shippingRate',
  'numberOfEmployees',
  'numberOfRooms',
  'numberOfBedrooms',
  'numberOfBathroomsTotal',
  'numberOfItems',
  'totalJobOpenings',
  'offerCount',
  'ratingCount',
  'reviewCount',
  'userInteractionCount',
  'maximumAttendeeCapacity',
  'remainingAttendeeCapacity',
  'occupancy',
]);
const literal = (value: JsonValue): JsonValue =>
  value && typeof value === 'object' && !Array.isArray(value) && '@value' in value ? value['@value']! : value;
// JSON-LD permits numeric text, including prices. Other text is not a numeric amount to compare.
function numeric(value: JsonValue | undefined): number | undefined {
  if (value === undefined) return;
  const raw = literal(value);
  if (typeof raw !== 'number' && !(typeof raw === 'string' && raw.trim())) return;
  const number = Number(raw);
  return Number.isFinite(number) ? number : undefined;
}
function monetary(node: JsonLdObject | undefined): boolean {
  const types = node?.['@type'];
  return (Array.isArray(types) ? types : [types]).some(
    (type) => typeof type === 'string' && type.replace(/^https?:\/\/schema\.org\//, '') === 'MonetaryAmount',
  );
}

/** Checks supplied values only. Incomplete objects and overnight opening hours remain valid. */
export function validateJsonLdValues(
  node: JsonLdObject,
  field: string,
  property?: string,
  parent?: JsonLdObject,
): Array<{ field: string; message: string }> {
  const errors: Array<{ field: string; message: string }> = [];
  const nonnegativeValue =
    monetary(node) || nonnegativeProperties.has(property ?? '') || (property === 'value' && monetary(parent));
  for (const [name, child] of Object.entries(node)) {
    const values = Array.isArray(child) ? child : [child];
    values.forEach((entry, index) => {
      if (entry === null) return;
      const path = `${field}.${name}${Array.isArray(child) ? `.${index}` : ''}`;
      const value = literal(entry);
      if (name === 'email' && !emailSchema.safeParse(value).success)
        errors.push({ field: path, message: 'Use a valid email address.' });
      if (
        timeProperties.has(name) &&
        (typeof value !== 'string' || !dateOrTimeSchema.safeParse(`2000-01-01T${value}`).success)
      )
        errors.push({ field: path, message: 'Use a valid time, with an optional UTC offset.' });
      if (
        nonnegativeProperties.has(name) ||
        (nonnegativeValue && ['value', 'minValue', 'maxValue'].includes(name))
      ) {
        const number = numeric(entry);
        if (number !== undefined && number < 0)
          errors.push({ field: path, message: 'Use a nonnegative amount or count.' });
      }
    });
  }
  for (const [min, max] of [
    ['minValue', 'maxValue'],
    ['lowPrice', 'highPrice'],
  ] as const) {
    const lower = numeric(node[min]),
      upper = numeric(node[max]);
    if (lower !== undefined && upper !== undefined && lower > upper)
      errors.push({ field: `${field}.${max}`, message: `${max} must not be below ${min}.` });
  }
  return errors;
}
